// Pure derivation of the tournament tables the player is currently active at,
// for the YourTables strip on lobby load.
//
// Context: LOBBY_DATA_RECEIVED delivers a cash-games-only payload (see
// dispatchLobbyCashGames). To also show the player's active MTT / SnG in the
// YourTables strip on a page refresh — and to keep their broadcasts flowing
// while the player sits on the lobby — we merge tournament rows into
// state.playerGames AND into the SubscribeToGameUpdates loop from the
// LOBBY_DATA_RECEIVED handler. state.lobby.playerTournamentDetails carries
// the per-player, per-tournament data (assigned gameID + status), so we
// derive tournament rows from there.
//
// The caller's per-lobby-mount dedup (subscribedThisLobbyMount) guarantees
// each derived gameID is subscribed at most ONCE per Lobby mount — the
// previous auto-subscribe effects re-emitted on every ~1 Hz playerTournament-
// Details push, which was the spam we retired. Emitting from this handler
// instead (where the same dedup ref already exists for cash tables) reuses
// the working machinery without reintroducing the collision.

export type ActiveTournamentTable = {
  // `gameID` is always the NORMALIZED numeric id (validated via Number.isFinite
  // + > 0 in the loop below). Narrowed from the previous `string | number` so
  // it matches PlayerGame.gameID and cannot silently disagree with the dedup
  // key derived from the same value.
  gameID: number;
  game: string;
  tabletype: 'm' | 't';
};

export function deriveActiveTournamentTables(
  playerTournamentDetails: any[] | null | undefined,
  sitAndGoList: any[] | null | undefined,
  isRecentlyExited: (gameID: string) => boolean
): ActiveTournamentTable[] {
  if (!playerTournamentDetails || playerTournamentDetails.length === 0) return [];

  // Every entry in sitAndGoList is a SnG tournament — used to tag rows as
  // tabletype 't'. Anything not in the SnG list is treated as MTT ('m').
  const sngTournamentIds = new Set<number>(
    (sitAndGoList || [])
      .map((s: any) => Number(s?.tournamentId ?? s?.id))
      .filter((n) => Number.isFinite(n))
  );

  const seen = new Set<string>();
  const rows: ActiveTournamentTable[] = [];

  for (const detail of playerTournamentDetails) {
    // Status 'R' (Registered, not yet started) and 'P' (Playing) are the two
    // states that warrant showing the player's seat in "Your Tables". 'F'
    // (Finished / busted out) is intentionally excluded — the tournament card
    // itself is what tells them how the run ended.
    if (detail?.status !== 'R' && detail?.status !== 'P') continue;

    const gidNum = Number(detail?.gameID);
    // Registered-but-not-yet-seated rows (backend inserts `tournament_stats`
    // with `gameID = -1` at register-time; the seat-assignment update sets
    // gameID + status='P' together) are INTENTIONALLY skipped here — there is
    // no table to hydrate yet, no `gotTexas`/`gotOmaha` fan-out to warrant a
    // SubscribeToGameUpdates. The tournament CARD in the lobby covers the
    // pre-start UX. Once the engine seats the player, the next
    // gotPlayerTournamentDetails push carries the assigned gameID and the row
    // enters this loop normally. (This dropped the "SnG chip on refresh
    // pre-start" behaviour that was in the legacy findRegisteredSngsToSubscribe
    // helper — reviewed and accepted as the correct trade-off for the branch's
    // subscribe-only-when-focused product spec.)
    if (!Number.isFinite(gidNum) || gidNum <= 0) continue;

    const gidStr = String(gidNum);
    if (isRecentlyExited(gidStr)) continue;
    // De-dup: a player can only be seated at one table per tournament, but
    // guard anyway so a duplicate detail row doesn't produce duplicate strip
    // chips.
    if (seen.has(gidStr)) continue;
    seen.add(gidStr);

    const tabletype: 'm' | 't' = sngTournamentIds.has(Number(detail?.tournamentId))
      ? 't'
      : 'm';

    // `game` is INTENTIONALLY defaulted to 'texas' when the record does not
    // carry it. The `gotPlayerTournamentDetails` payload projects only
    // {tournamentId, status, gameID, tournamentStatus, isMultiFlightBreak,
    // gameClosed} — it does not carry a `game` variant column, by design.
    // Downstream `SubscribeToGameUpdates` is idempotent with respect to the
    // wrong variant (the server 404s on the mismatch harmlessly), and the
    // real table hydration flows through `Game.tsx` on navigation, which
    // reads the variant from the URL. We do NOT resolve the variant from
    // sitAndGoList/tournamentList here — the branch scope is "make the strip
    // display on refresh"; variant resolution belongs to Game.tsx.
    rows.push({
      gameID: gidNum,
      game: detail?.game || 'texas',
      tabletype
    });
  }

  return rows;
}
